import React, { ForwardedRef, forwardRef, useCallback } from 'react';

import { cn } from '../../../lib/css/className/className';
import { IconArrowDown as IconArrowBottom } from '../../../icons/colorless';
import { HStack, VStack } from '../../Stack';
import { CommonTrigger } from '../CommonTrigger/CommonTrigger';
import { getSelectTriggerContent, getSelectTriggerHelp } from '../utils';
import { HelpProps } from '../../Help';
import { useTypographyWithSkin } from '../../../hooks/useTypography/useTypographyWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { TextGenericWithSkin } from '../../TextGeneric/TextGenericWithSkin';
import { useTheme } from '../../../lib/theme/ThemeProvider';
import { TextStubWithSkin } from '../../Text';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';
import { SelectColor } from '../commonTypes';
import { SkinColor, SkinRole } from '../../../lib/skin/getColorSkin/generated/types';

import { TriggerProps } from './types';

import './Trigger.css';

export const cnTrigger = cn('SelectTrigger');

const skinMap: Record<SelectColor, SkinColor | SkinRole> = {
    contour: 'gray-transparent-contour',
    contrast: 'gray-neutral',
    normal: 'gray-low-contrast',
};

/**
 * Дефолтный триггер для селекта. Можно использовать, если нужно переопределить поведение селекта.
 * @example
 * ```tsx
 * <Select
 *  renderTrigger={props =>
 *      <SelectTrigger {...props} value={[{ value: 'item1', content: 'Custom trigger content' }]} />
 *  }
 * />
 * ```
 */
export const SelectTrigger = forwardRef(function SelectTrigger<ValueType extends string>(
    props: TriggerProps<ValueType>,
    ref: ForwardedRef<HTMLElement>,
) {
    const {
        size,
        value,
        isDisabled,
        isOpened,
        placeholder,
        isTruncated,
        isStub,
        width: triggerWidth,
        showCaption,
        color,
        addonRight,
    } = props;

    const textSize = 'current-main + current-caption';
    const { textClasses: iconTextClasses } = useTypographyWithSkin({
        color: 'current',
        size: textSize,
        role: 'placeholder',
    });
    const { textClasses: iconLeftTextClasses } = useTypographyWithSkin({
        color: 'current',
        size: textSize,
    });
    const colorSkinClass = getColorSkin(skinMap[color], {
        isDisabled: isDisabled,
    });
    const { addonClasses } = useInternalVerticalAlignWithSkin({
        textSize,
        addonType: 'universal',
    });

    const { iconSize } = useTheme().sizeTokens[size];
    const iconLeft = value.length === 1 && value[0]?.iconLeft;
    const isShowCaption = showCaption && value.length === 1 && value[0]?.caption !== undefined;

    const helpValue = useCallback(
        (helpProps: HelpProps) => {
            return getSelectTriggerHelp(value)?.({ as: 'span', ...helpProps });
        },
        [value],
    );

    const selectTriggerContent = getSelectTriggerContent(value);

    return (
        <CommonTrigger {...props} ref={ref} className={cnTrigger({ size }, [colorSkinClass])}>
            {isStub ? (
                <VStack className={cnTrigger('stub')}>
                    <TextStubWithSkin
                        skinSize={size}
                        height={'current-main'}
                        width={
                            triggerWidth === 'auto'
                                ? 36
                                : // auto - из css, подразумевает ширину в 100%
                                  'auto'
                        }
                    />
                </VStack>
            ) : (
                <>
                    {iconLeft && (
                        <VStack
                            justifyContent={'center'}
                            alignItems={'center'}
                            className={cnTrigger('icon', [addonClasses, iconLeftTextClasses])}
                        >
                            {iconLeft}
                        </VStack>
                    )}
                    <TextGenericWithSkin
                        className={cnTrigger('value')}
                        textSize={textSize}
                        skinSize={size}
                        color={'current'}
                        caption={isShowCaption && value[0]?.caption}
                        isTruncated={isTruncated}
                        help={helpValue}
                        role={selectTriggerContent ? undefined : 'placeholder'}
                    >
                        {selectTriggerContent || placeholder}
                    </TextGenericWithSkin>
                </>
            )}
            <HStack
                justifyContent={'flex-end'}
                alignItems={'center'}
                gap="8"
                className={cnTrigger('addon-right-wrapper', [addonClasses])}
            >
                {addonRight}
                <IconArrowBottom
                    size={iconSize}
                    className={cnTrigger('arrow', { opened: isOpened }, [iconTextClasses])}
                />
            </HStack>
        </CommonTrigger>
    );
});
